import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Package, Award, Heart, LogOut, MapPin } from "lucide-react";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === null) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
  }, [user]);

  if (!user) return <div className="container-furn py-24 text-center text-muted-foreground">Loading…</div>;

  const tabs = [["overview", User, "Overview"], ["orders", Package, "Orders"], ["loyalty", Award, "Loyalty"]];

  return (
    <div className="container-furn py-12">
      <div className="mb-8 flex flex-col justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-end">
        <div>
          <span className="overline">My Account</span>
          <h1 className="mt-1 font-display text-4xl font-bold uppercase tracking-tight">Hi, {user.name.split(" ")[0]}</h1>
        </div>
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 self-start border border-border px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-destructive hover:text-destructive transition-colors"><LogOut className="h-4 w-4" /> Sign Out</button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        <aside className="flex gap-2 overflow-x-auto no-scrollbar lg:flex-col">
          {tabs.map(([val, Icon, label]) => (
            <button key={val} onClick={() => setTab(val)} data-testid={`account-tab-${val}`} className={`flex items-center gap-2.5 whitespace-nowrap border px-4 py-3 text-sm font-medium transition-colors ${tab === val ? "border-accent bg-accent/5 text-accent" : "border-border hover:border-accent/50"}`}><Icon className="h-4 w-4" /> {label}</button>
          ))}
          <Link to="/wishlist" className="flex items-center gap-2.5 whitespace-nowrap border border-border px-4 py-3 text-sm font-medium hover:border-accent/50 transition-colors"><Heart className="h-4 w-4" /> Wishlist</Link>
        </aside>

        <div>
          {tab === "overview" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="border border-border bg-card p-6"><span className="overline">Account</span><p className="mt-2 font-medium">{user.name}</p><p className="text-sm text-muted-foreground">{user.email}</p></div>
              <div className="border border-border bg-card p-6"><span className="overline">Loyalty Points</span><p className="mt-2 font-display text-3xl font-bold text-accent">{user.points}</p><p className="text-sm text-muted-foreground">£{(user.points / 100).toFixed(2)} to redeem</p></div>
              <div className="border border-border bg-card p-6"><span className="overline">Total Orders</span><p className="mt-2 font-display text-3xl font-bold">{orders.length}</p></div>
              <div className="border border-border bg-card p-6"><span className="overline flex items-center gap-1"><MapPin className="h-3 w-3" /> Default Store</span><p className="mt-2 text-sm">Unit 10, Blackhall Industrial Park, South Woodham Ferrers, CM3 5UW</p></div>
            </div>
          )}

          {tab === "orders" && (
            <div className="space-y-4">
              {orders.length === 0 ? <p className="text-muted-foreground">No orders yet. <Link to="/shop" className="text-accent">Start shopping</Link>.</p> :
                orders.map((o) => (
                  <div key={o.id} className="border border-border bg-card p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                      <div><p className="font-display font-semibold">#{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-GB")}</p></div>
                      <span className="bg-accent/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent">{o.status}</span>
                    </div>
                    <div className="flex items-center justify-between pt-3">
                      <p className="text-sm text-muted-foreground">{o.items.length} item(s) · {o.shipping.method}</p>
                      <span className="font-display font-bold">{gbp(o.total)}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {tab === "loyalty" && (
            <div className="border border-border bg-card p-8 text-center">
              <Award className="mx-auto h-12 w-12 text-accent" />
              <p className="mt-4 font-display text-5xl font-bold text-accent">{user.points}</p>
              <p className="mt-1 text-sm text-muted-foreground">Furn Reward Points</p>
              <p className="mx-auto mt-4 max-w-md text-sm text-foreground/80">Earn 1 point for every £1 spent. 100 points = £1 off your next order. Points are added automatically after every purchase.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
